import { useState, useMemo } from "react";
import { Box, Card, Typography, ToggleButton, ToggleButtonGroup, useTheme, IconButton, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Info, Check } from "lucide-react";
import { useTranslation } from "../../../../language";
import CustomDialogSmall from "../../../../components/Dialog/DiaCompSmall";
import TextInfoComp from "../ReuseComps/TextInfoComp";
import {
  ChartContainer,
  LinePlot,
  ChartsYAxis,
  ChartsGrid,
  useDrawingArea,
  useYScale,
} from "@mui/x-charts";

// ─────────────────────────────────────────────────────────────────────────────
// SHARED HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function statusColor(theme, status) {
  if (!status) return theme.palette.grey[400];
  if (status.toUpperCase() === "NORMAL") return theme.palette.traffic.green;
  return theme.palette.traffic.red;
}

function GaugeBar({ value, low, high }) {
  const theme = useTheme();
  if (value == null || (low == null && high == null)) return null;
  const val = Number(value);
  const lo  = low  != null ? Number(low)  : null;
  const hi  = high != null ? Number(high) : null;
  let dMin, dMax;
  if (lo != null && hi != null) {
    const range = (hi - lo) || 1;
    dMin = lo - range * 0.5;
    dMax = hi + range * 0.5;
  } else if (lo != null) {
    dMin = Math.min(0, lo * 0.5);
    dMax = Math.max(lo * 2, val * 1.2);
  } else {
    dMin = Math.min(0, val * 0.5);
    dMax = Math.max(hi * 1.5, val * 1.2);
  }
  const pad  = (dMax - dMin) * 0.05 || 1;
  dMin = Math.min(dMin, val - pad);
  dMax = Math.max(dMax, val + pad);
  const span     = dMax - dMin;
  const clamp    = v => Math.max(0, Math.min(100, ((v - dMin) / span) * 100));
  const lowPct   = lo != null ? clamp(lo) : 0;
  const highPct  = hi != null ? clamp(hi) : 100;
  const valPct   = clamp(val);
  const isNormal = (lo == null || val >= lo) && (hi == null || val <= hi);
  return (
    <Box sx={{ position: "relative", width: "100%", pb: 1 }}>
      <Box sx={{ position: "relative", width: "100%", height: 5, bgcolor: "action.disabledBackground", borderRadius: 0.5 }}>
        <Box sx={{ position: "absolute", left: `${lowPct}%`, width: `${highPct - lowPct}%`, height: "100%", bgcolor: theme.palette.traffic.green, opacity: 0.4 }} />
        <Box sx={{ position: "absolute", top: "50%", left: `${valPct}%`, transform: "translate(-50%, -50%)", width: 4, height: 14, bgcolor: isNormal ? theme.palette.traffic.green : theme.palette.traffic.red }} />
      </Box>
      {lo != null && (
        <Typography variant="caption" color="text.disabled" sx={{ position: "absolute", top: "100%", left: `${lowPct}%`, transform: "translateX(-50%)", fontSize: "0.6rem", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {lo.toFixed(2)}
        </Typography>
      )}
      {hi != null && (
        <Typography variant="caption" color="text.disabled" sx={{ position: "absolute", top: "100%", left: `${highPct}%`, transform: "translateX(-50%)", fontSize: "0.6rem", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {hi.toFixed(2)}
        </Typography>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// META
// ─────────────────────────────────────────────────────────────────────────────

const HORMONE_META = {
  E2:  { label: "Estradiol",    unit: "pg/ml",  role: "Mood & Energy" },
  P4:  { label: "Progesterone", unit: "ng/ml",  role: "Calm & Sleep" },
  LH:  { label: "LH",          unit: "mIU/ml", role: "Ovulation Trigger" },
  FSH: { label: "FSH",         unit: "mIU/ml", role: "Ovarian Signal" },
};

const PHASES = [
  { label: "Menstrual",  start: 1,  end: 5  },
  { label: "Follicular", start: 6,  end: 13 },
  { label: "Ovulatory",  start: 14, end: 16 },
  { label: "Luteal",     start: 17, end: 28 },
];


// ─────────────────────────────────────────────────────────────────────────────
// REFERENCE CURVE HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function gauss(d, peak, sigma, height, base) {
  return base + height * Math.exp(-0.5 * Math.pow((d - peak) / sigma, 2));
}

function buildCycleCurve(fn) {
  return Array.from({ length: 28 }, (_, i) => {
    const d = i + 1;
    const mid = fn(d);
    return { lo: mid * 0.5, mid, hi: mid * 1.6 };
  });
}

const CYCLE_CURVES = {
  E2:  buildCycleCurve(d => Math.max(30, gauss(d, 13, 3, 180, 40) + gauss(d, 21, 3, 80, 0))),
  P4:  buildCycleCurve(d => d < 14 ? 0.3 + d * 0.02 : Math.max(0.3, gauss(d, 21, 4, 14, 0.3))),
  LH:  buildCycleCurve(d => Math.max(2, gauss(d, 14, 1.2, 50, 3))),
  FSH: buildCycleCurve(d => Math.max(2, gauss(d, 5, 3, 5, 4) + gauss(d, 14, 1.5, 4, 0))),
};

// ─────────────────────────────────────────────────────────────────────────────
// EVALUATION
// ─────────────────────────────────────────────────────────────────────────────

function getEvaluation(hormoneKey, cycleDay, value) {
  if (value == null || cycleDay == null) return null;
  const curvePt = CYCLE_CURVES[hormoneKey]?.[cycleDay - 1];
  if (!curvePt) return null;

  let level = "NORMAL";
  if (value < curvePt.lo) level = "LOW";
  if (value > curvePt.hi) level = "HIGH";

  return { level };
}

// ─────────────────────────────────────────────────────────────────────────────
// CHART OVERLAY COMPONENTS  (must render inside ChartContainer)
// ─────────────────────────────────────────────────────────────────────────────

function PhaseBands() {
  const { left, top, width, height } = useDrawingArea();
  const theme = useTheme();
  const xPos = d => left + ((d - 1) / 27) * width;
  const bandFills = [
    alpha(theme.palette.text.primary, 0.04),
    alpha(theme.palette.text.primary, 0.02),
    alpha(theme.palette.text.primary, 0.04),
    alpha(theme.palette.text.primary, 0.02),
  ];

  return (
    <>
      {PHASES.map((ph, i) => (
        <g key={ph.label}>
          <rect
            x={xPos(ph.start)}
            y={top}
            width={xPos(ph.end + 1) - xPos(ph.start)}
            height={height}
            fill={bandFills[i]}
          />
          <text
            x={xPos((ph.start + ph.end) / 2)}
            y={top - 8}
            textAnchor="middle"
            fontSize="9"
            fontWeight="700"
            fill={theme.palette.text.secondary}
            fontFamily="system-ui"
          >
            {ph.label}
          </text>
        </g>
      ))}
    </>
  );
}

function MeasurementDot({ cycleDay, value, dotColor }) {
  const { left, top, width, height } = useDrawingArea();
  const yScale = useYScale();
  const theme  = useTheme();

  if (value == null || cycleDay == null || cycleDay < 1 || cycleDay > 28) return null;

  const cx = left + ((cycleDay - 1) / 27) * width;
  const cy = yScale(value);

  if (!Number.isFinite(cy) || cy < top || cy > top + height) return null;

  return (
    <g>
      <circle cx={cx} cy={cy} r={13} fill={dotColor} opacity={0.12}>
        <animate attributeName="r" values="10;15;10" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx={cx} cy={cy} r={7} fill={theme.palette.background.paper} stroke={dotColor} strokeWidth="2.5" />
      <circle cx={cx} cy={cy} r={3.5} fill={dotColor} />
    </g>
  );
}

function CycleDayBadge({ cycleDay, dotColor }) {
  const { left, top, width, height } = useDrawingArea();

  if (!cycleDay) return null;

  const cx = left + ((cycleDay - 1) / 27) * width;

  return (
    <g>
      <line x1={cx} x2={cx} y1={top} y2={top + height} stroke={dotColor} strokeWidth="1.5" strokeDasharray="4 3" opacity={0.65} />
      <rect x={cx - 20} y={top - 32} width={40} height={17} rx={4} fill={dotColor} />
      <text x={cx} y={top - 20} textAnchor="middle" fontSize="9" fontWeight="700" fill="white" fontFamily="system-ui">
        Day {cycleDay}
      </text>
      <line x1={cx} x2={cx} y1={top - 15} y2={top} stroke={dotColor} strokeWidth="1" opacity={0.5} />
    </g>
  );
}

function ReferenceBand({ hiData, loData, dotColor }) {
  const { left, width } = useDrawingArea();
  const yScale = useYScale();

  if (!yScale) return null;

  const xAt = i => (left + (i / 27) * width).toFixed(1);
  const yAt = v => yScale(v).toFixed(1);

  const topEdge    = hiData.map((v, i) => `${xAt(i)},${yAt(v)}`).join(" ");
  const bottomEdge = loData.map((v, i) => `${xAt(i)},${yAt(v)}`).reverse().join(" ");

  return (
    <polygon
      points={`${topEdge} ${bottomEdge}`}
      fill={alpha(dotColor, 0.22)}
      pointerEvents="none"
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CYCLE CHART
// ─────────────────────────────────────────────────────────────────────────────

const DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

function CycleChart({ hormoneKey, cycleDay, value }) {
  const theme = useTheme();
  const dotColor = theme.palette.primary.main;

  const curve = CYCLE_CURVES[hormoneKey];
  const hiData  = useMemo(() => curve.map(c => c.hi),  [curve]);
  const loData  = useMemo(() => curve.map(c => c.lo),  [curve]);
  const midData = useMemo(() => curve.map(c => c.mid), [curve]);

  const yMax = Math.max(...hiData, ...(value != null ? [value] : [])) * 1.15;

  const series = useMemo(() => [
    { id: "hi",  type: "line", data: hiData,  showMark: false, color: dotColor },
    { id: "lo",  type: "line", data: loData,  showMark: false, color: dotColor },
    { id: "mid", type: "line", data: midData, showMark: false, color: dotColor },
  ], [hiData, loData, midData, dotColor]);

  return (
    <Box sx={{ overflow: "hidden" }}>
      <ChartContainer
        series={series}
        xAxis={[{ id: "x", data: DAYS, scaleType: "linear", min: 0.5, max: 28.5 }]}
        yAxis={[{
          id: "y",
          min: 0,
          max: yMax,
          tickLabelStyle: { fill: theme.palette.text.secondary, fontSize: 10 },
        }]}
        height={300}
        margin={{ top: 46, right: 16, bottom: 8, left: 48 }}
        sx={{
          "& .MuiLineElement-series-hi":  { strokeOpacity: 0.55, strokeWidth: 1.5 },
          "& .MuiLineElement-series-lo":  { strokeOpacity: 0.55, strokeWidth: 1.5 },
          "& .MuiLineElement-series-mid": { strokeWidth: 2.5, strokeOpacity: 0.8, strokeDasharray: "5 3" },
          "& .MuiChartsAxis-directionX":  { display: "none" },
        }}
      >
        <PhaseBands />
        <ChartsGrid horizontal sx={{ "& line": { stroke: alpha(theme.palette.text.primary, 0.07) } }} />
        <ReferenceBand hiData={hiData} loData={loData} dotColor={dotColor} />
        <LinePlot />
        <MeasurementDot cycleDay={cycleDay} value={value} dotColor={dotColor} />
        <CycleDayBadge cycleDay={cycleDay} dotColor={dotColor} />
        <ChartsYAxis
          label={HORMONE_META[hormoneKey].unit}
          labelStyle={{ fill: theme.palette.text.secondary, fontSize: 10 }}
        />
      </ChartContainer>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN EXPORT
// Props:
//   latestValues — { E2, P4, LH, FSH } from the API (values in respective units)
//   cycleDay     — number (1–28) from the cycle API
// ─────────────────────────────────────────────────────────────────────────────

export default function HormoneCycleChart({ latestValues, cycleDay, markerResults }) {
  const [activeHormone, setActiveHormone] = useState("E2");
  const [infoOpen, setInfoOpen]           = useState(false);
  const [markerInfoOpen, setMarkerInfoOpen] = useState(false);
  const [markerInfoShort, setMarkerInfoShort] = useState(null);
  const theme = useTheme();
  const { t } = useTranslation();

  const dotColor   = theme.palette.primary.main;
  const cardStyles = theme.palette.mode === "light"
    ? { bgcolor: "grey.C50", borderColor: "grey.C900" }
    : { bgcolor: "greydark.C900", borderColor: "greydark.C400", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", boxShadow: "0 8px 32px rgba(0,0,0,0.6)" };

  const activeMeta   = HORMONE_META[activeHormone];
  const activeValue  = latestValues?.[activeHormone];
  const activeResult = markerResults?.[activeHormone];

  const statusKey  = `bt_status_${(activeResult?.status || "").toLowerCase()}`;
  const statusText = t(statusKey) !== statusKey ? t(statusKey) : (activeResult?.status ?? "");

  return (
    <Box sx={{ fontFamily: "system-ui, sans-serif" }}>

      {/* ── Section header ─────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.25 }}>
        <Box sx={{ width: 3, height: 18, borderRadius: 0.5, bgcolor: dotColor, flexShrink: 0 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary", letterSpacing: "-0.01em" }}>
          {t("hcc_title")}
        </Typography>
        <IconButton size="small" onClick={() => setInfoOpen(true)} sx={{ ml: 0.25, color: "text.disabled" }}>
          <Info size={18} />
        </IconButton>
        {cycleDay != null && (
          <Box sx={{ ml: "auto", px: 1.25, py: 0.4, borderRadius: 2, bgcolor: alpha(dotColor, 0.07) }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: dotColor }}>
              {t("hcc_day")} {cycleDay}
            </Typography>
          </Box>
        )}
      </Box>

      <CustomDialogSmall
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        title={t("hcc_title")}
        content={null}
      />

      {/* ── Chart card ─────────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ ...cardStyles, borderRadius: 4, px: "14px", pt: "12px", pb: "8px", mb: "14px" }}>

        {/* Hormone selector + label row */}
        <Typography variant="caption" sx={{ display: "block", fontWeight: 600, color: "text.secondary", mb: 0.75 }}>
          {t("hcc_select_marker")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.25 }}>
          <ToggleButtonGroup
            value={activeHormone}
            exclusive
            onChange={(_, val) => val && setActiveHormone(val)}
            size="small"
            sx={{
              gap: 0.5,
              "& .MuiToggleButtonGroup-grouped": { border: "none", borderRadius: "9px !important" },
            }}
          >
            {Object.keys(HORMONE_META).map(key => (
              <ToggleButton
                key={key}
                value={key}
                sx={{
                  px: 1.5,
                  py: 0.6,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  bgcolor: activeHormone === key ? dotColor : alpha(theme.palette.text.primary, 0.06),
                  color: activeHormone === key ? "common.white" : "text.secondary",
                  "&.Mui-selected": {
                    bgcolor: dotColor,
                    color: "common.white",
                    "&:hover": { bgcolor: alpha(dotColor, 0.85) },
                  },
                  "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.1) },
                }}
              >
                {key}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Typography variant="caption" sx={{ color: "text.disabled" }}>
            {activeMeta.label} · {t(`hcc_role_${activeHormone}`)}
          </Typography>
        </Box>

        <CycleChart hormoneKey={activeHormone} cycleDay={cycleDay} value={activeValue} />
      </Card>

      {/* ── Marker row ─────────────────────────────────────────────────── */}
      {activeResult && (
        <Box sx={{
          bgcolor: theme.palette.mode === "light" ? "grey.C50" : "greydark.C900",
          borderRadius: "14px",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", px: 2.5, py: 1.25, gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
            <Box sx={{ width: 10, height: 30, flexShrink: 0, bgcolor: statusColor(theme, activeResult.status) }} />
            <Box sx={{ flexGrow: 1, minWidth: 120 }}>
              <Typography variant="body1" fontWeight={500}>{t(`markername_${activeResult.marker_short}`)}</Typography>
              <Typography variant="caption" color="text.secondary">{t(`markerdescr_${activeResult.marker_short}`)}</Typography>
            </Box>
            <Typography variant="number3" fontWeight={400} sx={{ minWidth: 80, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
              {Number(activeResult.value).toFixed(2)}{activeResult.unit ? ` ${activeResult.unit}` : ""}
            </Typography>
            <Box sx={{ minWidth: 140, ml: 2, display: "flex", alignItems: "center" }}>
              <GaugeBar value={activeResult.value} low={activeResult.ref_low} high={activeResult.ref_high} />
            </Box>
            <Box sx={{ minWidth: 68, display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
              {activeResult.status === "NORMAL"
                ? <Check size={18} strokeWidth={3} color={theme.palette.traffic.green} />
                : <Typography variant="subtitle2" fontWeight={700} sx={{ color: statusColor(theme, activeResult.status) }}>{statusText}</Typography>
              }
            </Box>
            <Tooltip title="Info" placement="right">
              <IconButton size="small" onClick={() => { setMarkerInfoShort(activeResult.marker_short); setMarkerInfoOpen(true); }} sx={{ "&:hover": { transform: "scale(1.2)" } }}>
                <Info size={18} color={theme.palette.secondary.main} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {!latestValues && (
        <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.disabled", textAlign: "center" }}>
          {t("hcc_no_data")}
        </Typography>
      )}

      <CustomDialogSmall
        open={markerInfoOpen}
        onClose={() => { setMarkerInfoOpen(false); setMarkerInfoShort(null); }}
        title="Information"
        content={markerInfoShort && (
          <TextInfoComp
            description={`description_${markerInfoShort}`}
            lowtext={`lowtext_${markerInfoShort}`}
            lowrec={`lowrec_${markerInfoShort}`}
            rangetext={`rangetext_${markerInfoShort}`}
            hightext={`hightext_${markerInfoShort}`}
            highrec={`highrec_${markerInfoShort}`}
          />
        )}
      />
    </Box>
  );
}
